import { AnyNetworkElement } from '../types/network';
import { networkApi } from '../infrastructure/api/NetworkApiClient';
import { ApiError } from '../infrastructure/api/BaseApiClient';

export interface NetworkData {
  elements: AnyNetworkElement[];
  mapPosition?: {
    center: [number, number];
    zoom: number;
  };
}

/**
 * Load network data from the API.
 * @param zoneId - Optional zone ID. If provided, loads data for that specific zone
 *                 without affecting the global zone selection. If not provided,
 *                 uses the currently selected global zone.
 */
export async function loadNetworkData(zoneId?: string): Promise<NetworkData> {
  try {
    if (zoneId) {
      // Load from specific zone without affecting global state
      return await networkApi.httpGet<NetworkData>(`/zones/${zoneId}/network`);
    }
    // Use global zone selection
    return await networkApi.getNetwork<NetworkData>();
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 403) {
        throw new Error('Access denied. Please check your permissions and try again.', { cause: error });
      }
      throw new Error(`Failed to load network data: ${error.message}`, { cause: error });
    }
    throw error;
  }
}

// Delete all network data for the current zone
export async function deleteAllNetworkData(): Promise<{success: boolean; error?: string}> {
  try {
    await networkApi.zoneDelete<{ message: string }>('/network');
    return {success: true};
  } catch (error) {
    console.error('[NetworkDataService] Failed to delete all network data:', error);
    if (error instanceof ApiError) {
      return {success: false, error: error.message};
    }
    return {success: false, error: 'Failed to delete network data'};
  }
}

// Update a single network element
// Note: The backend expects a POST with the complete element (including id in body)
export async function updateNetworkElement(id: string, completeElement: AnyNetworkElement): Promise<{success: boolean; error?: string}> {
  try {
    await networkApi.updateElement(id, completeElement);
    return {success: true};
  } catch (error) {
    console.error('[NetworkDataService] Failed to update element:', error);
    if (error instanceof ApiError) {
      return {success: false, error: error.message};
    }
    return {success: false, error: 'Failed to update element'};
  }
}
